import { CircleAlert } from "lucide-react";

import type { OverviewResponse } from "@/shared/api";

import { attention, evidenceInvestigations } from "./attention";
import type { OverviewSources } from "./sources";
import { PANEL_TITLE } from "@/design-system/Panel";

/**
 * What needs a look, and nothing at all when nothing does. A failure is a
 * warning; an incomplete cycle record is not one — the bot trades on — so it
 * is a quiet folded line, never an alarm beside a healthy reading.
 */
export function ActionRequired({
  overview,
  sources,
}: {
  readonly overview: OverviewResponse;
  readonly sources?: OverviewSources | undefined;
}): React.JSX.Element | null {
  const issues = attention(overview, sources);
  const records = evidenceInvestigations(overview);
  if (issues.length === 0 && records.length === 0) return null;
  return (
    <div className="flex flex-col gap-2">
      {issues.length > 0 && <section
        aria-label="Требует проверки"
        data-testid="action-required"
        className="rounded-panel border border-status-warning bg-surface-default px-4 py-3"
      >
        <div className="flex items-start gap-3">
          <CircleAlert aria-hidden="true" className="mt-0.5 size-4 shrink-0 text-status-warning" />
          <div className="min-w-0">
            <h2 className={PANEL_TITLE}>Требует проверки: {issues.length}</h2>
            <ul className="mt-1 flex flex-col gap-0.5 text-helper-sm text-text-secondary">
              {issues.map((issue) => <li key={issue}>{issue}</li>)}
            </ul>
          </div>
        </div>
      </section>}
      {records.length > 0 && <section
        aria-label="Неполные записи циклов"
        data-testid="incomplete-records"
        className="rounded-panel border border-border-subtle bg-surface-default px-4 py-2"
      >
        <details className="text-helper-sm text-text-secondary">
          <summary className="cursor-pointer select-none">
            Неполные записи циклов: {records.length} — на торговлю не влияет
          </summary>
          <ul className="mt-1 flex flex-col gap-0.5">
            {records.map((record) => <li key={record}>{record}</li>)}
          </ul>
        </details>
      </section>}
    </div>
  );
}
